import { useAppDispatch } from "hooks/hooks";
import { useSelector } from "react-redux";
import { setRegistrationTachographPopoverState } from "../registrationAndTachograph.slice";
import { selectResourceDetails, updateLastKnownUsageAmount, updateNextInspectionDate } from "../../Details/details.slice";
import { useState } from "react";
import { InspectionType } from "Dashboard/scenes/Resources/domain/InspectionType";
import { ICreateInspectionRequestParams } from "Dashboard/scenes/Resources/domain/ICreateInspectionRequestParams";
import { Controller, useForm } from "react-hook-form";
import { FormatDateIso } from "utils/dateUtils";
import { resourcesServices } from "Dashboard/scenes/Resources/services/resource.service";
import { toast } from "react-toastify";
import { unitName } from "Dashboard/scenes/Resources/helpers/resourceUtil";
import DatePicker from "react-datepicker";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLongArrowAltDown } from "@fortawesome/free-solid-svg-icons";
import popoverClasses from "../../../styles/RegistrationTachographModal.module.scss";
import classes from "../../../../../../components/Popover/popover.module.scss";
import { inspectionTypeDictionary } from "utils/translationDictionary";
import NumberInput from "Dashboard/components/NumberInput";
import { FormatNumber } from "utils/numberUtils";

interface IProps {
  onModalChange: Function;
}

const RegistrationTachographPopover = ({ onModalChange }: IProps) => {
  const dispatch = useAppDispatch();

  const resourceDetails = useSelector(selectResourceDetails);

  const [clickedTimes, setClickedTimes] = useState(0);
  const [incorrectUsage, setIncorrectUsage] = useState<boolean>(false);
  const [currentUsage, setCurrentUsage] = useState<number>(0);

  const [inspectionType, setInspectionType] = useState<InspectionType>(
    InspectionType.REGISTRATION
  );

  const close = () => {
    dispatch(setRegistrationTachographPopoverState({ isOpen: false }));
    reset();
  };

  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
    reset,
  } = useForm<ICreateInspectionRequestParams>();

  const onChangeUsage = (newValue: number) => {
    setCurrentUsage(newValue);
    if (resourceDetails && clickedTimes > 0) {
      setIncorrectUsage(newValue > resourceDetails?.resource.lastKnownUsageAmount);
    }
  };

  const firstClick = () => {
    setClickedTimes(clickedTimes + 1);
    if (resourceDetails) {
      if (currentUsage > resourceDetails?.resource.lastKnownUsageAmount) {
        setIncorrectUsage(true);
      }
    }
  };

  const onSubmit = async (data: ICreateInspectionRequestParams) => {
    if (resourceDetails) {
      if (resourceDetails?.resource.lastKnownUsageAmount < currentUsage) {
        setIncorrectUsage(true);
        toast.error(`Unesite ${resourceDetails?.resource.usageUnit.name === "km" ?
          "manju kilometražu ili jednaku poslednje unetoj" :
          "manje radne sate ili jednake poslednje unetim"} (${FormatNumber(resourceDetails?.resource.lastKnownUsageAmount)} ${resourceDetails?.resource.usageUnit.name})!`);
        return;
      }
      try {
        const inspection = await resourcesServices.createInspection(
          resourceDetails?.resource.resourceId,
          data
        );
        dispatch(updateLastKnownUsageAmount(data.usageAmount));
        dispatch(updateNextInspectionDate(inspection));
        toast.success(`${inspectionTypeDictionary[inspectionType]} je uspešno ${[InspectionType.REGISTRATION, InspectionType.FIRST_AID_KIT].includes(inspectionType) ? 'uneta' : 'unet'}.`);
        onModalChange();
        close();
      } catch (err) {
        toast.error("Neočekivana greška, proverite da li ste sve dobro uneli.");
        console.error(err);
      }
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={`
        ${popoverClasses.registrationPopover} ${classes.basicPopover}`}>
      <div className={classes.row}>
        <div className={`${classes.col} ${classes.colDir}`}>
          <label className={classes.aboveLabel}>jedinica mere</label>
          <input
            type="text"
            className={`${classes.itemBorder} 
              ${classes.textMedium}
              ${classes.transparent}`}
            value={
              resourceDetails && unitName(resourceDetails?.resource.usageUnit)
            }
            readOnly
            disabled
          />
        </div>
      </div>

      <div className={classes.row}>
        <div className={`${classes.col} ${classes.colDir}`}>
          <label className={classes.aboveLabel}>urađena na</label>
          <Controller
            name="usageAmount"
            control={control}
            defaultValue={undefined}
            rules={{
              required: true,
            }}
            render={({ field: { onChange } }) => (
              <NumberInput
                formatAs="quantity"
                onChange={(value: string, unmaskedValue: string, parsedValue: number) => {
                  onChange({ target: { value: parsedValue } });
                  onChangeUsage(parsedValue);
                }}
                placeholder={resourceDetails?.resource.lastKnownUsageAmount !== undefined
                  ? FormatNumber(resourceDetails.resource.lastKnownUsageAmount)
                  : "0"}
                className={`${classes.itemBorder}
                        ${errors.usageAmount || incorrectUsage === true ? classes.itemError : ""}`}
              />
            )}
          />
        </div>
      </div>

      <div className={classes.row}>
        <div className={`${classes.col} ${classes.colDir}`}>
          <label className={classes.aboveLabel}>cena</label>
          <Controller
            name="price"
            control={control}
            defaultValue={undefined}
            rules={{
              required: true,
            }}
            render={({ field: { onChange } }) => (
              <NumberInput
                formatAs="rsd"
                onChange={(value: string, unmaskedValue: string, parsedValue: number) => {
                  onChange({ target: { value: parsedValue } });
                }}
                className={`${classes.itemBorder}
                  ${errors.price ? classes.itemError : ""}`}
              />
            )}
          />
        </div>
      </div>

      <div className={classes.row}>
        <div className={`${classes.col} ${classes.colDir}`}>
          <label className={classes.aboveLabel}>datum</label>
          <Controller
            control={control}
            name="date"
            defaultValue={undefined}
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <DatePicker
                className={`${classes.dateItem} ${classes.itemBorder} ${classes.fullWidth}
                  ${errors.date ? classes.itemError : ""}`}
                onChange={(date) => {
                  onChange(date ? FormatDateIso(date) : undefined);
                }}
                showMonthDropdown
                showYearDropdown
                dateFormat="dd.MM.yyyy"
                selected={value ? new Date(value) : null}
              />
            )}
          />
        </div>
      </div>

      <div className={classes.row}>
        <div className={`${classes.col} ${classes.colDir}`}>
          <label className={classes.aboveLabel}>
            izaberite vrstu usluge
          </label>
          <div className={`${popoverClasses.serviceType} 
            ${errors.type ? classes.itemError : ""}`}>
            {[InspectionType.REGISTRATION,
            InspectionType.FIRE_EXTINGUISHER,
            InspectionType.TACHOGRAPH,
            InspectionType.DIGITAL_TACHOGRAPH,
            InspectionType.FIRST_AID_KIT,
            InspectionType.TECHNICAL_INSPECTION,
            InspectionType.FULL_COVERAGE_INSURANCE].map((type: InspectionType) => (
              <span key={type}>
                <input
                  type="radio"
                  value={type}
                  {...register("type", {
                    required: true,
                    onChange: (e) => { setInspectionType(e.target.value) }
                  })}
                />
                <label>{inspectionTypeDictionary[type]}</label>
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className={classes.row}>
        <div className={`${classes.col} ${classes.colDir}`}>
          <label className={classes.aboveLabel}>napomena</label>
          <input
            className={`${classes.itemBorder}`}
            type="text" spellCheck="false" {...register("note")}></input>
        </div>
      </div>

      <div className={classes.row}>
        <div className={classes.buttons}>
          <button onClick={() => clickedTimes === 0
            ? firstClick() : null}
            className={classes.iconButton} type="submit">
            <FontAwesomeIcon
              icon={faLongArrowAltDown}
              className={classes.icon}
            />
          </button>
        </div>
      </div>
    </form>
  );
};

export default RegistrationTachographPopover;
